/**
 * PLT-7780: Eevo's cookieless conversion funnel, from the landing page (uprightproject.com/eevo) to
 * sign-up, first decision, MCP connection and purchase.
 *
 * Nothing is stored on the visitor's device. The landing page sends one beacon per page view and
 * per click on a link to Eevo (`POST /api/arvo/funnel/hit`), and adds a random visit id (`ev`) and
 * the A/B variants it showed (`evv`) to those links. The console sends a beacon when it loads, with
 * the `ev` it arrived with. The server keys each hit by a keyed hash of what the request itself
 * carries (`upserve/modules/arvo/funnel/visitor-key.ts`), and at sign-up links the new account to
 * the landing visit it came from (`attribution.ts`). Decisions, MCP connections and purchases are
 * read from Eevo's own tables, so they need no beacon.
 *
 * PLT-8038: an Upright blog post about Eevo can send a `content_view` beacon per page view too, and
 * tags its links to Eevo with `EEVO_CONTENT_SOURCE` and its id as the campaign, so the funnel page
 * shows each post's readers and the visits and sign-ups that came through it. Content views are kept
 * out of the funnel's steps: a blog reader is not a landing visitor.
 *
 * The facade keeps a copy of the parameter names and the beacon payload (upright-facade
 * `src/facade/src/arvo-consequential-decision-model/funnel.ts`): keep the two in step.
 */

/** The visit id the landing page adds to its links to Eevo. */
export const EEVO_FUNNEL_VISIT_PARAM = 'ev';
/** The landing page's A/B variants, as `experiment.variant` pairs joined by `,`. */
export const EEVO_FUNNEL_VARIANTS_PARAM = 'evv';

export const EEVO_FUNNEL_HIT_EVENTS = ['landing_view', 'cta_click', 'console_view', 'content_view'] as const;
export type EevoFunnelHitEvent = (typeof EEVO_FUNNEL_HIT_EVENTS)[number];

/**
 * PLT-8038: the `utm_source` of the links from an Upright blog post to Eevo. The link's
 * `utm_campaign` is the post's id, the same id its `content_view` beacons send as `target`, which
 * is how the funnel page puts a post's readers and its click-throughs on one row.
 */
export const EEVO_CONTENT_SOURCE = 'upright-blog';

/** Experiment and variant keys: lowercase letters, digits and `-`. */
export const EEVO_EXPERIMENT_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,47}$/;

/** The body of `POST /api/arvo/funnel/hit`, sent as `text/plain` JSON so a beacon needs no preflight. */
export interface EevoFunnelHitPayload {
  event: EevoFunnelHitEvent;
  /** The landing page's visit id: made on the page for its own hits, from `ev` on the console's. */
  visitId?: string;
  /** The page's path, without its query. */
  path: string;
  /** `document.referrer`; only its host is kept. */
  referrer?: string;
  utm?: Partial<Record<EevoUtmKey, string>>;
  /** Experiment key → the variant shown. */
  variants?: Record<string, string>;
  /**
   * For `cta_click`: the link's `utm_content`, which names the button. For `content_view`: the
   * post's id, e.g. `decision-models-beyond-cheap-decisions`.
   */
  target?: string;
  /**
   * PLT-7831: the page's address carried LinkedIn's click id (`li_fat_id`), which LinkedIn adds to
   * the link of an ad that was clicked. Only that it was there is sent: the id itself never leaves
   * the visitor's browser, and nothing is sent to LinkedIn.
   */
  linkedinClick?: boolean;
  /**
   * PLT-7870: for a `console_view` of the billing page, the promo code its link filled in
   * (`/billing?code=…`, `./promo-codes.ts`), so a promo link's visits count without UTM tags.
   */
  promoCode?: string;
}

/** The query parameter LinkedIn adds to an ad's link when the ad account has click ids switched on. */
export const LINKEDIN_CLICK_ID_PARAM = 'li_fat_id';

/** The source of a visit that arrived from a click on a LinkedIn ad (`linkedinClick`). */
export const EEVO_LINKEDIN_ADS_SOURCE = 'linkedin ads';

export const EEVO_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export type EevoUtmKey = (typeof EEVO_UTM_KEYS)[number];

/** Parses `evv` (`hero.b,cta.control`) into experiment → variant; malformed pairs are left out. */
export function parseEevoVariantsParam(value: string | null | undefined): Record<string, string> {
  const variants: Record<string, string> = {};
  for (const pair of (value ?? '').split(',').slice(0, 8)) {
    const [experiment, variant] = pair.split('.');
    if (
      experiment &&
      variant &&
      EEVO_EXPERIMENT_KEY_PATTERN.test(experiment) &&
      EEVO_EXPERIMENT_KEY_PATTERN.test(variant)
    ) {
      variants[experiment] = variant;
    }
  }
  return variants;
}

export type EevoExperimentStatus = 'draft' | 'running' | 'stopped';

export interface EevoExperimentVariant {
  key: string;
  label: string;
  /** A relative share of page views; 0 shows the variant only when forced with `?eevo_variant=`. */
  weight: number;
}

/** `GET /api/arvo/funnel/experiments` (public): the running experiments, for the landing page. */
export interface EevoPublicExperiment {
  key: string;
  variants: Array<{ key: string; weight: number }>;
}

export interface EevoExperiment {
  key: string;
  name: string;
  status: EevoExperimentStatus;
  variants: EevoExperimentVariant[];
  startedAt: string | null;
  stoppedAt: string | null;
}

/**
 * How a sign-up was linked to where it came from:
 * - `invite`: the account was made through a person's invite link (`arvo_referral`, PLT-7791);
 *   exact, and ahead of the others, as the invite is why the person came, whatever page they saw;
 * - `link`: the landing visit's id came along on the link to Eevo (exact);
 * - `visitor_key`: a landing view with the same visitor key, address included, this week;
 * - `loose_key`: a landing view with the same visitor key without the address, in the last 7 days;
 * - `console_only`: no landing view, but the console visit before sign-up says where it came from;
 * - `none`: nothing to go on (no beacon reached us, or the account predates tracking).
 */
export const EEVO_ATTRIBUTION_METHODS = ['invite', 'link', 'visitor_key', 'loose_key', 'console_only', 'none'] as const;
export type EevoAttributionMethod = (typeof EEVO_ATTRIBUTION_METHODS)[number];

/** The source of a sign-up, or a console visit, through an invite link. */
export const EEVO_INVITE_SOURCE = 'invite';

export const EEVO_FUNNEL_RANGES = ['7d', '30d', '90d', '365d', 'all'] as const;
export type EevoFunnelRangeId = (typeof EEVO_FUNNEL_RANGES)[number];

/** The funnel's steps, in order. Each counts accounts (or visitors) on the day they reached it. */
export interface EevoFunnelCounts {
  /** Distinct visitor keys that viewed the landing page. */
  landingVisitors: number;
  /** Distinct landing visits that clicked through to Eevo. */
  ctaClicks: number;
  /** Distinct visitor keys that opened Eevo's console. */
  consoleVisitors: number;
  /** Accounts created. */
  signups: number;
  /** Accounts that made their first decision. */
  firstDecisions: number;
  /** Accounts that first connected an MCP client (OAuth grant or MCP/connector decision). */
  mcpConnected: number;
  /** Accounts that bought credit for the first time. */
  purchases: number;
}

export type EevoFunnelStep = keyof EevoFunnelCounts;

export interface EevoFunnelDay extends EevoFunnelCounts {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
}

export interface EevoFunnelSourceRow {
  /** `utm_source`, else the referring site, else `direct`. */
  source: string;
  landingVisitors: number;
  signups: number;
  firstDecisions: number;
  purchases: number;
}

/**
 * PLT-7831: one tagged link, i.e. one `utm_source` / `utm_campaign` / `utm_content` combination, so
 * each post or ad with its own link gets its own row. Visits without a campaign or content tag are
 * left out: the sources table covers them.
 */
export interface EevoFunnelLinkRow {
  source: string;
  campaign: string | null;
  content: string | null;
  landingVisitors: number;
  signups: number;
  firstDecisions: number;
  purchases: number;
}

/**
 * PLT-7870: one promo code. Its link visitors are the console visitors who opened a link filling in
 * the code; the account columns count the accounts that redeemed it in the range, and how far those
 * accounts have got, whenever they got there, as the tagged links do.
 */
export interface EevoFunnelPromoRow {
  code: string;
  linkVisitors: number;
  redemptions: number;
  /** Redeeming accounts created in the range: new users the code brought, not existing ones using it. */
  newSignups: number;
  firstDecisions: number;
  purchases: number;
}

/**
 * PLT-8038: one blog post that sends `content_view` beacons, or whose tagged links brought visits.
 * `clickThroughs` are the distinct visitors who opened the landing page or the console through one
 * of the post's links; the account columns count the sign-ups linked to such a visit, and how far
 * those accounts have got, as the tagged links do.
 */
export interface EevoFunnelContentRow {
  /** The post's id: its `content_view` target and its links' `utm_campaign`. */
  content: string;
  /** The page the post's views came from, e.g. `/blog/<id>/`; null when only its links were seen. */
  path: string | null;
  readers: number;
  clickThroughs: number;
  signups: number;
  firstDecisions: number;
  purchases: number;
}

export interface EevoFunnelVariantResult {
  key: string;
  label: string;
  weight: number;
  landingVisitors: number;
  ctaClicks: number;
  signups: number;
  firstDecisions: number;
  mcpConnected: number;
  purchases: number;
}

export interface EevoFunnelExperimentResult extends Omit<EevoExperiment, 'variants'> {
  variants: EevoFunnelVariantResult[];
}

/** Per-day counts of one experiment's variants, in the order of `variants`. */
export interface EevoFunnelVariantSeries {
  experimentKey: string;
  variants: Array<{ key: string; label: string }>;
  days: Array<{ date: string; landingVisitors: number[]; ctaClicks: number[]; signups: number[] }>;
}

/** A UTC day, `YYYY-MM-DD`, as the funnel's `series` dates are. */
export const EEVO_FUNNEL_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `GET /api/admin/arvo-business/funnel?range=…&day=…&includeInternal=…&includeUnattributed=…`.
 * `day` (PLT-7876), a UTC day in the range, narrows everything but `series` and `variantSeries`
 * to that day: the chart keeps the whole range so another day can be picked.
 */
export interface EevoFunnelSummary {
  generatedAt: string;
  range: EevoFunnelRangeId;
  /** The day the counts are narrowed to; null for the whole range. */
  day: string | null;
  includeInternal: boolean;
  /** False: the account steps count only accounts linked to a tracked visit. */
  includeUnattributed: boolean;
  /** The first hit ever recorded; null before the beacons have sent anything. */
  trackingSince: string | null;
  totals: EevoFunnelCounts;
  /** The same span just before the range; null for all time and for a day. */
  previousTotals: EevoFunnelCounts | null;
  series: EevoFunnelDay[];
  /** The range's (or day's) sign-ups by how they were linked to their source. */
  attribution: Record<EevoAttributionMethod, number>;
  sources: EevoFunnelSourceRow[];
  links: EevoFunnelLinkRow[];
  promoCodes: EevoFunnelPromoRow[];
  /** PLT-8038: blog posts, by readers and by the visits and sign-ups through their links. */
  content: EevoFunnelContentRow[];
  experiments: EevoFunnelExperimentResult[];
  /** The latest running experiment's variants per day; null when none runs. */
  variantSeries: EevoFunnelVariantSeries | null;
}

/** The account steps whose accounts the funnel page lists, from a click on the step's bar (PLT-7797). */
export const EEVO_FUNNEL_LISTED_STEPS = ['signups', 'firstDecisions'] as const;
export type EevoFunnelListedStep = (typeof EEVO_FUNNEL_LISTED_STEPS)[number];

/** The most accounts one list returns, newest first; `total` says how many there were. */
export const EEVO_FUNNEL_ACCOUNTS_LIMIT = 500;

export interface EevoFunnelAccountRow {
  organizationId: number;
  name: string;
  kind: 'personal' | 'organization';
  /** The account's first admin's email, else its first member's; null for an account without members. */
  email: string | null;
  signupAt: string;
  firstDecisionAt: string | null;
  method: EevoAttributionMethod;
  /** As in the funnel's sources; null when the account was linked to none. */
  source: string | null;
}

/**
 * `GET /api/admin/arvo-business/funnel/accounts?step=…&range=…&day=…&includeInternal=…&includeUnattributed=…`:
 * the accounts a step counts in the range (or the day), under the same switches as the summary.
 */
export interface EevoFunnelAccounts {
  step: EevoFunnelListedStep;
  rows: EevoFunnelAccountRow[];
  total: number;
}

/** A tag value as the dashboard groups it: trimmed and lowercased; null when empty. */
export function normalizeUtmValue(value: string | null | undefined): string | null {
  const normalized = value?.trim().toLowerCase();
  return normalized || null;
}

/**
 * Where a visit came from, for the dashboard: `linkedin ads` for a click on a LinkedIn ad, else
 * `utm_source`, else the referring site, else `direct`. A LinkedIn ad click wins over the tags, so
 * a post's link tagged `utm_source=linkedin` still splits into its ad clicks and its other clicks.
 */
export function eevoFunnelSource(utmSource: string | null, referrerHost: string | null, linkedinClick = false): string {
  if (linkedinClick) return EEVO_LINKEDIN_ADS_SOURCE;
  const utm = normalizeUtmValue(utmSource);
  if (utm) return utm;
  const host = referrerHost
    ?.trim()
    .toLowerCase()
    .replace(/^www\./, '');
  if (!host) return 'direct';
  // `com.linkedin.android` is the LinkedIn Android app's referrer (`android-app://com.linkedin.android/`).
  if (host === 'lnkd.in' || host.endsWith('linkedin.com') || host.startsWith('com.linkedin.')) return 'linkedin';
  if (host === 't.co' || host === 'x.com' || host === 'twitter.com') return 'x';
  if (/(^|\.)google\.[a-z.]+$/.test(host)) return 'google';
  if (/(^|\.)bing\.com$/.test(host)) return 'bing';
  if (host === 'duckduckgo.com') return 'duckduckgo';
  if (host === 'chatgpt.com' || host === 'chat.openai.com') return 'chatgpt';
  if (host === 'claude.ai') return 'claude';
  if (host === 'perplexity.ai') return 'perplexity';
  if (host === 'news.ycombinator.com') return 'hacker news';
  return host;
}
